// 📄 src/pages/Payment/CheckoutPage.jsx

import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosClient from "../../api/axiosClient";
import { generateClientRequestId, track } from "../../utils/analytics";
import { toast } from "react-toastify";
import {
  CreditCard,
  Check,
  ArrowLeft,
  ShieldCheck,
  Lock,
  Zap,
  Gift,
  ChevronDown,
} from "lucide-react";

// --- STATIC PLAN DATA (Source of Truth) ---
const STATIC_PLAN_DATA = {
  Basic: {
    priceMonthly: 999,
    currency: "INR",
    description: "Entry plan for small teams and starter campaigns.",
  },
  Growth: {
    priceMonthly: 2799,
    currency: "INR",
    description: "Best fit for scaling automation, campaigns and catalog flows.",
  },
  Pro: {
    priceMonthly: 3799,
    currency: "INR",
    description: "Advanced flows, assignment, webhooks, and higher scale.",
  },
  Enterprise: {
    priceMonthly: null,
    currency: "INR",
    description: "Custom solution for high-volume and enterprise support needs.",
  },
};

const PLAN_ALIASES = {
  Starter: "Basic",
};
const CANONICAL_BACKEND_PLAN_KEYS = new Set(["Basic", "Growth", "Pro"]);

const CYCLE_CONFIG = {
  Monthly: { months: 1, discountPct: 0, label: "1 Month (Monthly Billing)" },
  Quarterly: {
    months: 3,
    discountPct: 8,
    label: "3 Months (Quarterly Billing - Save 8%)",
  },
  Yearly: {
    months: 12,
    discountPct: 20,
    label: "12 Months (Yearly Billing - Save 20%)",
  },
};

// Server-driven pricing endpoints:
// - preview returns computed totals (coupon + tax + final)
// - create checkout session returns checkoutSessionId used for status polling
const PREVIEW_ENDPOINT = "/payment/checkout/preview";
const CREATE_CHECKOUT_ENDPOINT = "/payment/subscribe/checkout";
const MISMATCH_OR_COUPON_ERROR_CODES = new Set([
  "PROMO_PREVIEW_MISMATCH",
  "CHECKOUT_PREVIEW_MISMATCH",
  "COUPON_INVALID",
  "COUPON_EXPIRED",
  "COUPON_LIMIT_REACHED",
  "PRICE_CHANGED",
  "PROMOTION_COMMIT_FAILED",
]);

const normalizeCycle = raw => {
  const c = String(raw || "").trim().toLowerCase();
  if (c === "quarterly") return "Quarterly";
  if (c === "yearly") return "Yearly";
  return "Monthly";
};

const formatAmount = (amount, currency) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);

const formatRemainingTime = remainingMs => {
  if (remainingMs == null || remainingMs <= 0) return "00:00";
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
};

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [couponCode, setCouponCode] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [mismatchNotice, setMismatchNotice] = useState(false);
  const [sessionExpiresAtUtc, setSessionExpiresAtUtc] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const clientRequestIdRef = useRef(generateClientRequestId());
  const analyticsBaseProps = {
    clientRequestId: clientRequestIdRef.current,
    source: "checkout",
  };

  // Retrieve state passed from BillingPage
  const {
    planKey: incomingPlanKey,
    backendPlanKey: incomingBackendPlanKey,
    billingCycle: initialCycle,
  } = location.state || {};

  const displayPlanKey = PLAN_ALIASES[incomingPlanKey] || incomingPlanKey;
  const rawBackendPlanKey = incomingBackendPlanKey || incomingPlanKey;
  const backendPlanKey = CANONICAL_BACKEND_PLAN_KEYS.has(displayPlanKey)
    ? displayPlanKey
    : rawBackendPlanKey;

  // Local state for the cycle (allows changing it on this page)
  const [selectedCycle, setSelectedCycle] = useState(normalizeCycle(initialCycle));

  // Redirect back if no plan selected
  useEffect(() => {
    if (!displayPlanKey) {
      toast.warn("Please select a plan first.");
      navigate("/app/settings/billing");
    }
  }, [displayPlanKey, navigate]);

  // Derived Values
  const selectedPlanData = STATIC_PLAN_DATA[displayPlanKey];
  const currency = previewResult?.currency || selectedPlanData?.currency || "INR";
  const cycleConfig = CYCLE_CONFIG[selectedCycle];
  const monthlyPrice =
    typeof selectedPlanData?.priceMonthly === "number"
      ? selectedPlanData.priceMonthly
      : 0;

  const toNumber = value => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const totals = {
    // Server-driven amounts (no client-side pricing math).
    baseAmount: toNumber(previewResult?.baseAmount ?? previewResult?.subtotalAmount),
    cycleDiscountAmount: toNumber(previewResult?.cycleDiscountAmount),
    discountAmount: toNumber(previewResult?.discountAmount),
    gstAmount: toNumber(previewResult?.gstAmount ?? previewResult?.taxAmount),
    finalAmount: toNumber(previewResult?.finalAmount),
  };

  const previewSessionExpiresAtUtc =
    previewResult?.expiresAtUtc ||
    previewResult?.expiresAt ||
    previewResult?.expiresAtUtcString ||
    null;
  const sessionExpiryMs = sessionExpiresAtUtc
    ? new Date(sessionExpiresAtUtc).getTime()
    : null;
  const sessionRemainingMs =
    sessionExpiryMs != null && Number.isFinite(sessionExpiryMs)
      ? sessionExpiryMs - nowMs
      : null;
  const isSessionLocallyExpired =
    sessionRemainingMs != null && sessionRemainingMs <= 0;
  const sessionRemainingLabel =
    sessionRemainingMs == null ? null : formatRemainingTime(sessionRemainingMs);

  const hasPreview = !!previewResult;
  const formatPreviewAmount = amount =>
    hasPreview ? formatAmount(amount, currency) : "—";

  useEffect(() => {
    if (!sessionExpiresAtUtc || isSessionLocallyExpired) return undefined;
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, [isSessionLocallyExpired, sessionExpiresAtUtc]);

  useEffect(() => {
    if (!isSessionLocallyExpired) return;
    setCheckoutError("Checkout session expired. Please try again.");
    // Clear stale session expiry so user can create a fresh session on next click.
    setSessionExpiresAtUtc(null);
  }, [isSessionLocallyExpired]);

  const unwrapResponse = res => {
    const body = res?.data ?? {};
    const ok = typeof body.ok === "boolean" ? body.ok : true;
    const data = body?.data ?? body;
    const message =
      body?.message || data?.message || (!ok ? "Request failed." : "");
    return { ok, data, message };
  };

  const normalizeCheckoutError = err => {
    const status = err?.response?.status ?? err?.status ?? null;
    const body = err?.response?.data ?? err?.data ?? {};
    const code = body?.code || body?.errorCode || err?.code || "";
    const message =
      body?.message ||
      err?.message ||
      "Failed to create checkout session.";
    return {
      status,
      code: String(code || "").trim().toUpperCase(),
      message,
    };
  };

  const isMismatchOrCouponError = ({ status, code, message }) => {
    if (MISMATCH_OR_COUPON_ERROR_CODES.has(code)) return true;

    const text = String(message || "").toLowerCase();
    if (text.includes("mismatch")) return true;
    if (text.includes("price changed")) return true;
    if (text.includes("promotion") && text.includes("failed")) return true;
    if (
      text.includes("coupon") &&
      (text.includes("invalid") ||
        text.includes("expired") ||
        text.includes("limit") ||
        text.includes("not valid") ||
        text.includes("unavailable"))
    ) {
      return true;
    }

    return status === 409 && (text.includes("checkout") || text.includes("preview"));
  };

  const isInvalidCouponError = ({ code, message }) => {
    if (
      code === "COUPON_INVALID" ||
      code === "COUPON_EXPIRED" ||
      code === "COUPON_LIMIT_REACHED"
    ) {
      return true;
    }
    const text = String(message || "").toLowerCase();
    return (
      text.includes("coupon") &&
      (text.includes("invalid") ||
        text.includes("expired") ||
        text.includes("limit") ||
        text.includes("not valid"))
    );
  };

  // Preview flow:
  // This asks backend to compute pricing (coupon + tax + final amount) so UI does not
  // run pricing/coupon calculations locally anymore.
  const runPreview = async (coupon = "", { silent = false } = {}) => {
    if (!backendPlanKey) return;

    try {
      setPreviewLoading(true);
      setPreviewError("");

      const payload = {
        planId: backendPlanKey,
        billingCycle: selectedCycle,
      };
      const trimmedCoupon = coupon.trim();
      if (trimmedCoupon) payload.couponCode = trimmedCoupon;

      const res = await axiosClient.post(PREVIEW_ENDPOINT, payload);
      const { ok, data, message } = unwrapResponse(res);

      if (!ok) {
        setPreviewResult(null);
        const err = message || "Unable to preview pricing.";
        setPreviewError(err);
        void track("checkout_preview_failed", {
          planId: backendPlanKey,
          billingCycle: selectedCycle,
          couponCode: trimmedCoupon || null,
          status: res?.status ?? null,
          code:
            res?.data?.code || res?.data?.errorCode || data?.code || data?.errorCode || "",
          message: err,
        }, analyticsBaseProps);
        if (!silent) toast.error(err);
        return false;
      }

      const nextPreview = {
        baseAmount: toNumber(data?.baseAmount ?? data?.subtotalAmount),
        cycleDiscountAmount: toNumber(data?.cycleDiscountAmount),
        discountAmount: toNumber(data?.discountAmount),
        gstAmount: toNumber(data?.gstAmount ?? data?.taxAmount),
        finalAmount: toNumber(data?.finalAmount),
        currency: data?.currency || selectedPlanData?.currency || "INR",
        couponId: data?.couponId ?? null,
        couponCode: data?.couponCode || trimmedCoupon || null,
        message: data?.message || message || "",
      };
      setPreviewResult(nextPreview);
      void track("checkout_preview_success", {
        planId: backendPlanKey,
        billingCycle: selectedCycle,
        couponCode: nextPreview.couponCode,
        couponId: nextPreview.couponId,
        baseAmount: nextPreview.baseAmount,
        discountAmount: nextPreview.discountAmount,
        taxAmount: nextPreview.gstAmount,
        finalAmount: nextPreview.finalAmount,
        currency: nextPreview.currency,
      }, analyticsBaseProps);
      return true;
    } catch (err) {
      setPreviewResult(null);
      const errMsg =
        err?.response?.data?.message || "Failed to fetch pricing preview.";
      setPreviewError(errMsg);
      void track("checkout_preview_failed", {
        planId: backendPlanKey,
        billingCycle: selectedCycle,
        couponCode: coupon.trim() || null,
        status: err?.response?.status ?? null,
        code:
          err?.response?.data?.code || err?.response?.data?.errorCode || err?.code || "",
        message: errMsg,
      }, analyticsBaseProps);
      if (!silent) toast.error(errMsg);
      return false;
    } finally {
      setPreviewLoading(false);
    }
  };

  // Auto-preview on first load and whenever plan/cycle changes.
  useEffect(() => {
    if (!displayPlanKey || !backendPlanKey) return;
    runPreview(couponCode, { silent: true });
    // couponCode intentionally excluded to avoid preview on every key stroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPlanKey, backendPlanKey, selectedCycle]);

  // Handlers
  const handleApplyCoupon = async () => {
    setMismatchNotice(false);
    setSessionExpiresAtUtc(null);
    const ok = await runPreview(couponCode);
    if (ok) {
      toast.success("Pricing preview updated.");
    }
  };

  const handleProceedToPay = async () => {
    try {
      setCheckoutLoading(true);
      setCheckoutError("");
      setMismatchNotice(false);

      if (isSessionLocallyExpired) {
        setCheckoutError("Checkout session expired. Please try again.");
        setSessionExpiresAtUtc(null);
      }

      if (!hasPreview) {
        toast.warn("Please wait for pricing preview to load.");
        return;
      }

      const payload = {
        planId: backendPlanKey,
        billingCycle: selectedCycle,
      };
      // Prefer couponId from preview if backend provides it.
      if (previewResult?.couponId) {
        payload.couponId = previewResult.couponId;
      } else if (couponCode.trim()) {
        payload.couponCode = couponCode.trim();
      }

      const res = await axiosClient.post(CREATE_CHECKOUT_ENDPOINT, payload);
      const { ok, data, message } = unwrapResponse(res);

      if (!ok) {
        const normalized = normalizeCheckoutError({
          status: res?.status,
          data: res?.data,
          message,
        });
        const mismatchDetected = isMismatchOrCouponError(normalized);
        void track("checkout_create_failed", {
          planId: backendPlanKey,
          billingCycle: selectedCycle,
          couponCode: couponCode.trim() || null,
          status: normalized.status,
          code: normalized.code,
          message: normalized.message,
          mismatchDetected,
        }, analyticsBaseProps);
        if (mismatchDetected) {
          const friendly =
            "Totals or coupon availability changed. We refreshed your checkout totals. Please review and try again.";
          setCheckoutError(friendly);
          setMismatchNotice(true);
          toast.warn(friendly);

          if (isInvalidCouponError(normalized)) {
            setCouponCode("");
            setPreviewResult(prev =>
              prev ? { ...prev, couponId: null, couponCode: null } : prev
            );
            toast.warn("Coupon removed because it’s no longer valid.");
            await runPreview("", { silent: false });
          } else {
            await runPreview(couponCode, { silent: false });
          }
          return;
        }

        const err = normalized.message || "Failed to create checkout session.";
        setCheckoutError(err);
        toast.error(err);
        return;
      }

      const nextSessionExpiresAtUtc =
        data?.expiresAtUtc ||
        data?.expiresAt ||
        data?.expiresAtUtcString ||
        previewSessionExpiresAtUtc ||
        null;
      setSessionExpiresAtUtc(nextSessionExpiresAtUtc);

      const checkoutSessionId =
        data?.checkoutSessionId || data?.sessionId || data?.id || null;

      if (!checkoutSessionId) {
        const err = "Checkout session id not returned by server.";
        setCheckoutError(err);
        toast.error(err);
        void track("checkout_create_failed", {
          planId: backendPlanKey,
          billingCycle: selectedCycle,
          couponCode: couponCode.trim() || null,
          status: res?.status ?? null,
          code: "CHECKOUT_SESSION_ID_MISSING",
          message: err,
          mismatchDetected: false,
        }, analyticsBaseProps);
        return;
      }

      void track("checkout_create_success", {
        checkoutSessionId,
        planId: backendPlanKey,
        billingCycle: selectedCycle,
        couponCode: couponCode.trim() || null,
        couponId: previewResult?.couponId || null,
        finalAmount: totals.finalAmount,
        currency,
      }, analyticsBaseProps);

      // Navigate to status page with checkoutSessionId so polling page can query
      // /payment/checkout/{checkoutSessionId}/status.
      navigate(
        `/app/payment/status?source=checkout&checkoutSessionId=${encodeURIComponent(checkoutSessionId)}&sessionId=${encodeURIComponent(checkoutSessionId)}&crid=${encodeURIComponent(clientRequestIdRef.current)}`
      );
    } catch (err) {
      console.error(err);
      const normalized = normalizeCheckoutError(err);
      const mismatchDetected = isMismatchOrCouponError(normalized);
      void track("checkout_create_failed", {
        planId: backendPlanKey,
        billingCycle: selectedCycle,
        couponCode: couponCode.trim() || null,
        status: normalized.status,
        code: normalized.code,
        message: normalized.message,
        mismatchDetected,
      }, analyticsBaseProps);
      if (mismatchDetected) {
        const friendly =
          "Totals or coupon availability changed. We refreshed your checkout totals. Please review and try again.";
        setCheckoutError(friendly);
        setMismatchNotice(true);
        toast.warn(friendly);

        if (isInvalidCouponError(normalized)) {
          setCouponCode("");
          setPreviewResult(prev =>
            prev ? { ...prev, couponId: null, couponCode: null } : prev
          );
          toast.warn("Coupon removed because it’s no longer valid.");
          await runPreview("", { silent: false });
        } else {
          await runPreview(couponCode, { silent: false });
        }
        return;
      }

      const errMsg = normalized.message || "Failed to initiate checkout.";
      setCheckoutError(errMsg);
      toast.error(errMsg);
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!displayPlanKey || !selectedPlanData) return null;

  return (
    <div className="bg-[#f5f6f7] min-h-[calc(100vh-80px)] py-10 px-4 sm:px-6 lg:px-8 font-sans">
      {/* 1. Header */}
      <div className="max-w-5xl mx-auto mb-8 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-emerald-700 text-sm font-medium transition-colors"
        >
          <ArrowLeft size={16} /> Back to Plans
        </button>
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Lock size={14} className="text-emerald-600" />
          <span className="font-semibold text-slate-700">Secure Checkout</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* LEFT COLUMN: Order Context (7 cols) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Plan Header Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center gap-3">
              <div className="p-2 bg-white rounded-md border border-slate-200 text-emerald-600">
                <Zap size={20} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wide">
                  Subscription Details
                </h2>
                <p className="text-xs text-slate-500">
                  You are upgrading to a premium experience
                </p>
              </div>
            </div>

            <div className="p-6">
              <h3 className="text-xl font-bold text-slate-900">
                {displayPlanKey} Plan
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                {selectedPlanData?.description}
              </p>
            </div>
          </div>

          {/* Billing Period Selector (New Section) */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm font-bold text-slate-800">
                Billing Period
              </label>
              {cycleConfig.discountPct > 0 && totals.cycleDiscountAmount > 0 && (
                <span className="bg-pink-100 text-pink-700 text-xs font-bold px-3 py-1 rounded-full">
                  SAVE {formatAmount(totals.cycleDiscountAmount, currency)} ({cycleConfig.discountPct}%)
                </span>
              )}
            </div>

            <div className="relative">
              <select
                value={selectedCycle}
                onChange={e => {
                  setSelectedCycle(e.target.value);
                }}
                className="w-full appearance-none bg-white border border-slate-300 text-slate-700 py-3 px-4 pr-8 rounded-lg leading-tight focus:outline-none focus:bg-white focus:border-emerald-500 font-medium cursor-pointer"
              >
                <option value="Monthly">1 Month (Monthly Billing)</option>
                <option value="Quarterly">
                  3 Months (Quarterly Billing - Save 8%)
                </option>
                <option value="Yearly">
                  12 Months (Yearly Billing - Save 20%)
                </option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-slate-500">
                <ChevronDown size={16} />
              </div>
            </div>

            <div className="mt-3 text-xs text-slate-500 flex justify-between">
                <span>
                  {selectedCycle === "Monthly"
                    ? "Renews automatically every month."
                    : selectedCycle === "Quarterly"
                      ? "Renews automatically every 3 months."
                      : "Renews automatically every year."}
                </span>
                <span className="font-medium text-slate-700">
                  {`${formatAmount(monthlyPrice, currency)} / month`}
                </span>
              </div>
            </div>

          {/* Trust Box */}
          <div className="bg-transparent rounded-xl border border-dashed border-slate-300 p-6 flex flex-col sm:flex-row items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-full shadow-sm">
                <ShieldCheck size={24} className="text-emerald-600" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-700">
                  Bank-Level Security
                </div>
                <div className="text-xs text-slate-500">
                  Your payment is encrypted and secured by Razorpay.
                </div>
              </div>
            </div>
            <div className="h-8 w-px bg-slate-200 hidden sm:block"></div>
            <div className="flex items-center gap-3">
              <div className="bg-white p-2 rounded-full shadow-sm">
                <CreditCard size={24} className="text-emerald-600" />
              </div>
              <div>
                <div className="text-sm font-bold text-slate-700">
                  All Cards Accepted
                </div>
                <div className="text-xs text-slate-500">
                  Credit, Debit, UPI, and Net Banking supported.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Payment Summary (5 cols) */}
        <div className="lg:col-span-5">
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 sticky top-8 overflow-hidden">
            <div className="p-6 bg-slate-50 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">
                Order Summary
              </h2>
            </div>

            <div className="p-6 space-y-5">
              {/* Coupon Input */}
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Gift size={12} /> Discount Code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="PROMO CODE"
                    disabled={checkoutLoading}
                    className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent uppercase placeholder:text-slate-400"
                  />
                  {previewResult?.couponCode ? (
                    <button
                      onClick={async () => {
                        setCouponCode("");
                        await runPreview("", { silent: true });
                      }}
                      className="px-3 py-2 bg-slate-100 text-slate-600 rounded-md text-xs font-bold hover:bg-slate-200 transition-colors"
                    >
                      REMOVE
                    </button>
                  ) : (
                    <button
                      onClick={handleApplyCoupon}
                      disabled={previewLoading || checkoutLoading}
                      className="px-4 py-2 bg-slate-800 text-white rounded-md text-xs font-bold hover:bg-slate-900 disabled:opacity-50 transition-colors"
                    >
                      {previewLoading ? "..." : "APPLY"}
                    </button>
                  )}
                </div>
                {previewResult?.couponCode && (
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600 font-medium bg-emerald-50 p-2 rounded border border-emerald-100">
                    <Check size={12} />
                    {previewResult?.message || "Code applied successfully"}
                  </div>
                )}
                {previewError && (
                  <div className="mt-2 text-xs text-red-600">{previewError}</div>
                )}
              </div>

              <hr className="border-slate-100" />

              {/* Calculations */}
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm text-slate-600">
                  <span>
                    {displayPlanKey} ({selectedCycle})
                  </span>
                  <span className="font-medium text-slate-900">
                    {formatPreviewAmount(totals.baseAmount)}
                  </span>
                </div>

                {totals.cycleDiscountAmount > 0 && (
                  <div className="flex justify-between items-center text-sm text-emerald-600">
                    <span>Billing cycle discount ({cycleConfig.discountPct}%)</span>
                    <span>
                      - {formatPreviewAmount(totals.cycleDiscountAmount)}
                    </span>
                  </div>
                )}

                {totals.discountAmount > 0 && (
                  <div className="flex justify-between items-center text-sm text-emerald-600">
                    <span>Coupon discount</span>
                    <span>
                      - {formatPreviewAmount(totals.discountAmount)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center text-sm text-slate-600">
                  <span>GST (18%)</span>
                  <span className="font-medium text-slate-900">
                    {formatPreviewAmount(totals.gstAmount)}
                  </span>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4 flex justify-between items-center border border-emerald-100">
                <div>
                  <div className="text-xs text-emerald-700 font-medium uppercase">
                    Total Payable
                  </div>
                  <div className="text-[10px] text-emerald-600">
                    Including all taxes
                  </div>
                </div>
                <div className="text-2xl font-bold text-emerald-700">
                  {formatPreviewAmount(totals.finalAmount)}
                </div>
              </div>
              {mismatchNotice && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                  Updated totals just now - please confirm before paying.
                </div>
              )}
              {previewLoading && (
                <div className="text-xs text-slate-500">Refreshing pricing preview…</div>
              )}
              {checkoutError && (
                <div className="text-xs text-red-600">{checkoutError}</div>
              )}
              {sessionExpiresAtUtc && !isSessionLocallyExpired && (
                <div className="text-xs text-slate-500">
                  Session expires in: {sessionRemainingLabel}
                </div>
              )}
              {isSessionLocallyExpired && (
                <div className="text-xs text-red-600">
                  Session expired, please start again.
                </div>
              )}

              <button
                onClick={handleProceedToPay}
                disabled={
                  checkoutLoading ||
                  previewLoading ||
                  !hasPreview ||
                  isSessionLocallyExpired
                }
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-lg shadow-md shadow-emerald-200 transition-all transform active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2 text-sm"
              >
                {checkoutLoading ? (
                  <span className="flex items-center gap-2">
                    Processing{" "}
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </span>
                ) : (
                  `Pay Securely ${formatPreviewAmount(totals.finalAmount)}`
                )}
              </button>
            </div>

            <div className="bg-slate-50 px-6 py-3 border-t border-slate-100 text-center">
              <p className="text-[10px] text-slate-400">
                By confirming, you agree to our Terms of Service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
